import React from "react";
import { IconX, IconArrowRight } from "@tabler/icons-react";

// Root cause tab body (RunDetail right column). `view` = rootcauseModel.buildRootCauseView(run).
// Pure render: state, polling and re-analyze live in RunDetail. Card v2 (APPROVED 2026-10-21): item,
// label, Failed, Why, View in transcript — nothing else. v3: a failed condition with a readable `label`
// shows it as the main line, the patient's value below and the raw expression in a collapsed <details>.
export default function RootCauseTab({ view, onReanalyze, reanalyzing, reanalyzeError, onViewTranscript }) {
  if (!view || !view.showTab) return null;
  const { state, header } = view;
  return (
    <>
      <div className="rc-head">
        {header && <span className="rc-score">{header.text}</span>}
        <span className="rc-head-right">
          {reanalyzeError && <span className="muted" style={{ fontSize: 12 }}>{reanalyzeError}</span>}
          <button className="btn sm" disabled={reanalyzing || view.reanalyzeDisabled} onClick={onReanalyze}>
            Re-analyze</button>
        </span>
      </div>
      {header?.banner && <div className="banner warn rc-banner">{header.banner}</div>}

      {(state === "pending" || state === "waiting" || state === "all_captured") && <p className="muted rc-state">{view.message}</p>}
      {(state === "error" || state === "unsupported") && (
        <div className="rc-state">
          <p style={{ margin: 0 }}>{view.message}</p>
          {view.reason && <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>{view.reason}</p>}
        </div>
      )}
      {state === "results" && (
        <>
          {view.partialNote && <div className="rc-partial">{view.partialNote}</div>}
          {view.sections.map((s) => (
            <div key={s.rubric} className="rc-section">
              <div className="rc-section-title">{s.title}</div>
              {s.cards.map((c) => <ItemCard key={c.key} c={c} onViewTranscript={onViewTranscript} />)}
            </div>
          ))}
        </>
      )}
    </>
  );
}

function FailedLine({ l }) {
  if (l.kind === "condition" && l.label) {
    // v3: the readable condition is the main line; the raw expression stays available, collapsed.
    return (
      <div className="rc-cond">
        <IconX size={13} className="rc-bad" aria-hidden />
        <div>
          <div className="rc-cond-label">{l.label}</div>
          {l.patient && <div className="rc-patient">patient: <b>{l.patient}</b></div>}
          {l.expression && (
            <details className="rc-raw">
              <summary>Expression</summary>
              <code className="rc-expr mono">{l.expression}</code>
            </details>
          )}
        </div>
      </div>
    );
  }
  if (l.kind === "condition") {
    return (
      <div className="rc-cond">
        <IconX size={13} className="rc-bad" aria-hidden />
        <div>
          <code className="rc-expr mono">{l.expression}</code>
          {l.patient && <span className="rc-patient rc-patient-inline">patient: <b>{l.patient}</b></span>}
        </div>
      </div>
    );
  }
  if (l.kind === "emulator") {
    return (
      <div className="rc-qa">
        {l.question && <div className="rc-q">{l.question}</div>}
        <div>Patient: <b>{l.answer}</b></div>
        {l.caseText && <div>Case: {l.caseText}</div>}
      </div>
    );
  }
  if (l.kind === "qa") {
    return (
      <div className="rc-qa">
        {l.question && <div className="rc-q">{l.question}</div>}
        {l.answer && <div>Patient: <b>{l.answer}</b></div>}
        {l.recorded && <div>Recorded: <span className="mono">{l.recorded}</span></div>}
      </div>
    );
  }
  return <div className="rc-text">{l.text}</div>;
}

function ItemCard({ c, onViewTranscript }) {
  return (
    <div className="rc-card">
      <div className="rc-item">{c.item}</div>
      <div className="rc-tag">{c.categoryLabel}{c.reasonLabel ? ` · ${c.reasonLabel}` : ""}</div>
      {c.failed.length > 0 && (
        <div className="rc-row">
          <span className="rc-k">Failed:</span>
          <div className="rc-v">{c.failed.map((l, i) => <FailedLine key={i} l={l} />)}</div>
        </div>
      )}
      {c.why && (
        <div className="rc-row">
          <span className="rc-k">Why:</span>
          <div className="rc-v">{c.why}</div>
        </div>
      )}
      {c.transcriptIndex != null && (
        <button className="btn sm rc-link" onClick={() => onViewTranscript(c.transcriptIndex)}>View in transcript <IconArrowRight size={12} /></button>
      )}
    </div>
  );
}
